<template>
  <div class="mt-2">
    <h1 class="text-b2 font-extrabold text-prime-green mb-2">
      {{ $t("components.sla.sla") }}
    </h1>
    <table>
      <tr>
        <td class="text-b3 text-prime-blue-dark pr-3.5 pb-1">
          {{ $t("components.slaContent.status").toUpperCase() }}:
        </td>
        <td class="text-b3 text-prime-grey-darkest align-top">
          {{ statusLabel.toUpperCase() }}
          <inline-svg
            :src="require('@/assets/circle.svg')"
            class="w-2.5 inline-block fill-current mx-2 align-baseline"
            :class="slaStatusColor(status)"
          />
        </td>
      </tr>
      <template v-for="period in periods" :key="period.key">
        <tr>
          <td
            class="text-b3 text-prime-blue-dark pr-3.5 pb-1 pt-3"
            colspan="2"
          >
            {{ period.label.toUpperCase() }}
            <inline-svg
              :src="require('@/assets/circle.svg')"
              class="w-2.5 inline-block fill-current mx-2 align-baseline"
              :class="
                period.active ? 'text-indicator-green' : 'text-prime-red-dark'
              "
            />
          </td>
        </tr>
        <tr>
          <td class="text-b3 text-green-2 pr-3.5 pb-1">
            {{ $t("components.slaContent.startsAt").toUpperCase() }}:
          </td>
          <td class="text-b3 text-prime-grey-darkest align-top">
            {{ formatDate(period.startsAt) }}
          </td>
        </tr>
        <tr>
          <td class="text-b3 text-green-2 pr-3.5 pb-1">
            {{ $t("components.slaContent.expires").toUpperCase() }}:
          </td>
          <td class="text-b3 text-prime-grey-darkest align-top">
            {{ formatDate(period.endsAt) }}
          </td>
        </tr>
      </template>
    </table>
    <p v-if="status === 'None'" class="text-b3 text-prime-grey-darkest mt-2">
      {{ $t("components.slaContent.noSla") }}
    </p>
    <div v-if="slaContent.description" class="mb-4">
      <p class="text-b3 text-prime-blue-dark pb-1">
        {{ $t("common.fields.description").toUpperCase() }}:
      </p>
      <div
        class="text-b3 text-prime-grey-darkest"
        v-html="slaContent.description"
      ></div>
    </div>
  </div>

  <div v-if="slaContent.files && slaContent.files.length > 0">
    <div class="flex flex-col w-full font-bold text-prime-green mb-4">
      <h1 class="uppercase">{{ $t("components.slaContent.files") }}</h1>
      <div class="flex flex-wrap items-start w-full">
        <template v-for="file in addIndexForImageViewer(slaContent.files)">
          <div
            v-if="file.imageIndex != null"
            :key="file.id"
            class="flex flex-col items-center w-1/2 p-4 sm:w-28 has-tooltip"
          >
            <img
              :src="
                route('api.files.content', {
                  id: file.id,
                  filename: file.originalName,
                  thumbnail: !!file.thumbnailUrl
                })
              "
              class="fill-current text-green-2 h-16 font-black inline-block cursor-pointer w-16 h-16"
              @click="
                viewImageObject(addIndexForImageViewer(slaContent.files), file)
              "
            />
            <span
              class="tooltip rounded shadow-xl p-1 bg-black text-white -mt-8"
              >{{ file.originalName }}</span
            >
          </div>
          <file
            v-else
            :key="file.id"
            :file="file"
            hide-file-name
            show-tooltip
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  isSlaActive,
  isPeriodActive,
  resolveSlaStatus,
  slaStatusColor,
  slaStatusTranslationKey,
} from "@/util/slaHelpers";
import File from "@/components/File.vue";
import "@/assets/css/viewer.css";
import { api as viewerApi } from "v-viewer";

export default {
  components: { File },

  props: ["slaContent"],

  computed: {
    // Prefer the status the server resolved so the page and the badge cannot disagree; fall back to
    // resolving locally for callers that pass a plain SLA content (e.g. the legacy location SLA).
    status() {
      return this.slaContent.status ?? resolveSlaStatus(this.slaContent);
    },
    statusLabel() {
      return this.$t(
        `components.slaContent.${slaStatusTranslationKey(this.status)}Short`
      );
    },
    periods() {
      const { startsAt, endsAt, extendedStartsAt, extendedEndsAt } =
        this.slaContent;

      const periods = [];

      if (startsAt || endsAt) {
        periods.push({
          key: "sla",
          label: this.$t("components.slaContent.sla"),
          startsAt,
          endsAt,
          active: isPeriodActive(startsAt, endsAt),
        });
      }

      if (extendedStartsAt || extendedEndsAt) {
        periods.push({
          key: "extendedSla",
          label: this.$t("components.slaContent.extendedSla"),
          startsAt: extendedStartsAt,
          endsAt: extendedEndsAt,
          active: isPeriodActive(extendedStartsAt, extendedEndsAt),
        });
      }

      return periods;
    },
  },

  methods: {
    isSlaActive,
    slaStatusColor,
    formatDate(date) {
      if (!date) return this.$t("common.placeholders.unknown");

      const options = { day: "numeric", month: "long", year: "numeric" };

      return new Date(date).toLocaleDateString(this.$i18n.locale, options);
    },
    addIndexForImageViewer(files) {
      let index = 0;
      return files.map((obj) => {
        let newObj = {
          ...obj,
          imageIndex: this.isImage(obj) ? index : null,
          url: this.getRoute(obj),
          alt: obj.originalName,
        };
        index++;
        return newObj;
      });
    },
    getRoute(file) {
      return this.route('api.files.content', {
        id: file.id,
        filename: file.originalName
      })
    },
    isImage(file) {
      var extension = file.extension.toLowerCase();
      if ([".png", ".jpg", ".jpeg"].includes(extension)) {
        return true;
      }
      return false;
    },
    viewImageObject(filesWithImageIndex, file) {
      let imageArray = filesWithImageIndex.filter((x) => x.imageIndex != null);
      viewerApi({
        options: {
          toolbar: true,
          url: "url",
          initialViewIndex: file.imageIndex,
          title: (image) => {
            return image.alt;
          },
        },
        images: imageArray,
      });
    },
  },
};
</script>
